import React from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { MailOutlined, LockOutlined, ShopOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/features/auth/api/auth-api';
import { useAuthStore } from '@/store/auth-store';
import { LanguageToggle } from '@/components/common/LanguageToggle';

interface RegisterFormValues {
    email: string;
    password: string;
    shopName: string;
}

export const RegisterPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const setAuth = useAuthStore((state) => state.setAuth);
    const [loading, setLoading] = React.useState(false);

    const onFinish = async (values: RegisterFormValues) => {
        setLoading(true);
        try {
            const res = await authApi.register(values);
            if (res.code === 200) {
                setAuth(res.data.token, {
                    username: res.data.username,
                    roles: res.data.roles,
                    permissions: res.data.permissions,
                    platformAdmin: res.data.platformAdmin ?? false,
                });
                // Confirm the email actually used so the user can verify what was registered
                // before being whisked into the dashboard. Old behaviour navigated immediately.
                message.success(t('auth.register_success_redirecting', { email: res.data.username }), 2.5);
                window.setTimeout(() => navigate('/'), 2500);
            } else {
                message.error(res.message || t('auth.register_failed'));
                setLoading(false);
            }
        } catch (error) {
            const detail = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
            message.error(detail || t('auth.register_failed'));
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-gray-100 px-4 relative">
            <div className="absolute top-4 right-4">
                <LanguageToggle />
            </div>
            <Card title={t('auth.register_title')} className="w-full max-w-96 shadow-lg">
                <p className="text-gray-500 text-sm mb-4">{t('auth.register_subtitle')}</p>
                <Form name="register_form" onFinish={onFinish} size="large" layout="vertical">
                    <Form.Item
                        name="email"
                        rules={[
                            { required: true, message: t('auth.email_required') },
                            { type: 'email', message: t('auth.email_invalid') },
                        ]}
                    >
                        <Input prefix={<MailOutlined />} placeholder={t('auth.email')} autoComplete="email" />
                    </Form.Item>
                    <Form.Item
                        name="shopName"
                        rules={[{ required: true, message: t('auth.shop_name_required') }]}
                    >
                        <Input prefix={<ShopOutlined />} placeholder={t('auth.shop_name')} maxLength={200} />
                    </Form.Item>
                    <Form.Item
                        name="password"
                        rules={[
                            { required: true, message: t('auth.password_required') },
                            { min: 6, message: t('auth.password_min_length') },
                        ]}
                    >
                        <Input.Password
                            prefix={<LockOutlined />}
                            placeholder={t('auth.password')}
                            autoComplete="new-password"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" className="w-full" loading={loading}>
                            {t('auth.register_button')}
                        </Button>
                    </Form.Item>

                    <div className="text-center text-sm text-gray-500">
                        {t('auth.already_have_account')}{' '}
                        <Link to="/login">{t('auth.go_to_login')}</Link>
                    </div>
                </Form>
            </Card>
        </div>
    );
};
